import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../AuthContext';
import { canManage, canEditProject } from '../permissions';
import ProjectForm from '../components/ProjectForm';
import Breadcrumbs from '../components/Breadcrumbs';
import BidLeveling from './BidLeveling';
import { formatBidNumber, formatRevLabel } from '../utils/bidNumber';
import { parseWallClock } from '../utils/datetime';
import DateTimePicker from '../components/DateTimePicker';
import PersonAutocomplete from '../components/PersonAutocomplete';
import '../styles/detail.css';
import '../styles/bidleveling.css';

function formatCurrency(val) {
  if (!val || val === 0) return '-';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(val);
}

function formatDate(val) {
  if (!val) return null;
  return new Date(val).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

function formatDateTime(val) {
  if (!val) return null;
  const s = String(val).replace('Z', '');
  const parts = s.split('T');
  const dp = parts[0].split('-');
  const tp = (parts[1] || '00:00').split(':');
  const d = new Date(parseInt(dp[0]), parseInt(dp[1]) - 1, parseInt(dp[2]), parseInt(tp[0]), parseInt(tp[1]) || 0);
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
    + ' at ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function daysUntil(val) {
  if (!val) return null;
  const now = new Date();
  const d = parseWallClock(val);
  return Math.ceil((d - now) / (1000 * 60 * 60 * 24));
}

const STATUS_CLASS = {
  'Bidding': 'status-bidding',
  'Pending': 'status-pending',
  'Budget': 'status-budget',
  'On Hold': 'status-on-hold',
  'Pipeline': 'status-pipeline',
  'Awarded': 'status-awarded',
  'Not Awarded': 'status-not-awarded',
};

const STATUS_DISPLAY = { 'Pipeline': 'Opportunities' };
function displayStatus(s) { return STATUS_DISPLAY[s] || s; }

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [editing, setEditing] = useState(false);
  const [activity, setActivity] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('details');
  const [alphaFlags, setAlphaFlags] = useState({ bidLeveling: false });
  const [revisions, setRevisions] = useState([]);
  const [creatingRevision, setCreatingRevision] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Per-card inline edit state. Each card independently toggles between read
  // and edit mode; Done persists that card's draft via the existing PUT which
  // auto-audits every changed field.
  const [editCards, setEditCards] = useState({ dates: false, info: false, client: false, team: false, financials: false, notes: false });
  const [draft, setDraft] = useState(null);
  const [savingCard, setSavingCard] = useState(null);
  const [lookups, setLookups] = useState({ projectTypes: [], ownerTypes: [], estimators: [], coordinators: [], pes: [], pms: [], allTeam: [], reviewers: [] });

  useEffect(() => {
    loadProject();
    loadActivity();
    loadRevisions();
    fetch('/api/public-settings/alpha-features', { credentials: 'same-origin' }).then(r => r.ok ? r.json() : {}).then(f => setAlphaFlags(prev => ({ ...prev, ...f }))).catch(() => {});
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    Promise.all([
      api.getProjectTypes(),
      api.getOwnerTypes(),
      api.getEstimators(),
      api.getCoordinators(),
      api.getPEs(),
      api.getPMs(),
      api.getTeam(),
      api.getReviewers(),
    ]).then(([projectTypes, ownerTypes, estimators, coordinators, pes, pms, allTeam, reviewers]) => {
      setLookups({ projectTypes, ownerTypes, estimators, coordinators, pes, pms, allTeam, reviewers });
    }).catch(() => {});
  }, []);

  function startEdit(card) {
    setDraft({ ...project });
    setEditCards(ec => ({ ...ec, [card]: true }));
  }

  function cancelEdit(card) {
    setEditCards(ec => ({ ...ec, [card]: false }));
    // Leave draft alone -- if another card is still open it needs the in-progress values.
  }

  function updateDraft(field, value) {
    setDraft(d => ({ ...d, [field]: value }));
  }

  async function saveCard(card) {
    if (!draft) return;
    setSavingCard(card);
    try {
      // PUT the full draft; the server's audit loop picks out the delta and
      // writes one ProjectActivity row per changed field (including CreatedBy).
      await api.updateProject(id, draft);
      await loadProject();
      await loadActivity();
      setEditCards(ec => ({ ...ec, [card]: false }));
    } catch (err) {
      alert('Save failed: ' + err.message);
    }
    setSavingCard(null);
  }


  async function loadProject() {
    setLoading(true);
    try {
      const data = await api.getProject(id);
      setProject(data);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }

  async function loadActivity() {
    try {
      const data = await api.getActivity(id);
      setActivity(data);
    } catch (err) {}
  }

  async function loadRevisions() {
    try {
      const data = await api.getRevisions(id);
      setRevisions(data);
    } catch (err) {}
  }

  async function handleCreateRevision() {
    const currentRev = project?.RevisionNumber || 1;
    const nextRev = currentRev + 1;
    if (!window.confirm(
      `Create ${formatRevLabel(nextRev)}?\n\n` +
      `All scopes, line items, and bids will be copied into the new revision. ` +
      `The current ${formatRevLabel(currentRev)} bids will be locked as historical record.`
    )) return;
    setCreatingRevision(true);
    try {
      const result = await api.createRevision(id);
      navigate(`/projects/${result.ProjectId}`);
    } catch (err) {
      alert('Error creating revision: ' + err.message);
      setCreatingRevision(false);
    }
  }

  async function handleDelete() {
    const rev = project?.RevisionNumber || 1;
    const displayBidNum = formatBidNumber(project.BidNumber, project.RevisionNumber);
    const isOnlyRevision = revisions.length <= 1;
    const msg = isOnlyRevision
      ? `Permanently delete ${displayBidNum} — ${project.ProjectName}?\n\nThis will delete the project and all associated data (scopes, bids, activity). This cannot be undone.`
      : `Delete revision ${formatRevLabel(rev)} of ${displayBidNum} — ${project.ProjectName}?\n\nThe previous revision will be unlocked and become editable. This cannot be undone.`;
    if (!window.confirm(msg)) return;
    setDeleting(true);
    try {
      const result = await api.deleteProject(id);
      if (result.previousRevisionId) {
        navigate(`/projects/${result.previousRevisionId}`);
      } else {
        navigate('/');
      }
    } catch (err) {
      alert('Error deleting: ' + err.message);
      setDeleting(false);
    }
  }

  async function handleSave(formData) {
    try {
      await api.updateProject(id, formData);
      await loadProject();
      setEditing(false);
    } catch (err) {
      alert('Error saving: ' + err.message);
    }
  }

  async function handleAddNote() {
    if (!newNote.trim()) return;
    try {
      await api.addActivity(id, { ActivityType: 'Note', Description: newNote });
      setNewNote('');
      await loadActivity();
    } catch (err) {
      alert('Error adding note: ' + err.message);
    }
  }

  if (loading) return <div className="loading">Loading...</div>;
  if (!project) return <div className="loading">Project not found</div>;

  if (editing) {
    return (
      <>
        <div className="top-bar">
          <h2>Edit: {project.ProjectName}</h2>
          <button className="btn btn-secondary" onClick={() => setEditing(false)}>Cancel</button>
        </div>
        <div className="page-content">
          <div className="card">
            <div className="card-body">
              <ProjectForm project={project} onSave={handleSave} onCancel={() => setEditing(false)} />
            </div>
          </div>
        </div>
      </>
    );
  }

  // Build timeline items
  const distributedValue = project.Distributed
    ? (project.DistributedAt
        ? `${formatDateTime(project.DistributedAt)}${project.DistributedBy ? ` by ${project.DistributedBy}` : ''}`
        : 'Distributed')
    : null;
  const timeline = [
    { label: 'ITB Received', value: formatDate(project.ITBReceived), raw: project.ITBReceived, icon: 'inbox', done: !!project.ITBReceived },
    { label: 'Plans Distributed', value: distributedValue, raw: project.DistributedAt || (project.Distributed ? new Date() : null), icon: 'distribute', done: !!project.Distributed },
    { label: 'Bid Walk Date', value: formatDate(project.BidWalkDate), raw: project.BidWalkDate, icon: 'walk', done: !!project.BidWalkDate && parseWallClock(project.BidWalkDate) < new Date() },
    { label: 'Sub Bids Due', value: formatDateTime(project.SubDueDate), raw: project.SubDueDate, icon: 'subs', done: !!project.SubDueDate && parseWallClock(project.SubDueDate) < new Date() },
    { label: 'Est. Bid Commencement', value: formatDate(project.EstBidStart), raw: project.EstBidStart, icon: 'start', done: !!project.EstBidStart && parseWallClock(project.EstBidStart) < new Date() },
    { label: 'Bid Due to Manager', value: formatDateTime(project.InternalDeadline), raw: project.InternalDeadline, icon: 'internal', done: !!project.InternalDeadline && parseWallClock(project.InternalDeadline) < new Date(), buffer: project.BufferHours },
    { label: 'Owner Bid Submission Deadline', value: formatDateTime(project.GCOwnerDeadline), raw: project.GCOwnerDeadline, icon: 'deadline', done: !!project.GCOwnerDeadline && parseWallClock(project.GCOwnerDeadline) < new Date() },
    { label: 'Date Bid Submitted to Owner', value: formatDateTime(project.DateBidSubmitted), raw: project.DateBidSubmitted, icon: 'submitted', done: !!project.DateBidSubmitted },
    { label: 'Date Awarded', value: formatDate(project.DateAwarded), raw: project.DateAwarded, icon: 'award', done: !!project.DateAwarded },
  ];

  const displayBid = formatBidNumber(project.BidNumber, project.RevisionNumber);
  const latestRevision = revisions.length > 0 ? revisions[revisions.length - 1] : null;
  const isLatest = !latestRevision || latestRevision.ProjectId === project.ProjectId;
  // Managers/admins, or this project's assigned Estimator/EC, may edit.
  const canEdit = canEditProject(user, project);

  return (
    <>
      <div className="top-bar">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <button className="btn btn-secondary btn-sm" onClick={() => navigate(-1)}>&larr; Back</button>
          <h2>{displayBid} &mdash; {project.ProjectName}</h2>
          <span className={`status-badge ${STATUS_CLASS[project.StatusName] || ''}`}>{displayStatus(project.StatusName)}</span>
          {project.IsPriority && (
            <span className="cm-priority-badge">
              <img src="/favicon.png" alt="" />MUST WIN
            </span>
          )}
          {revisions.length > 1 && (
            <div style={{ display: 'inline-flex', gap: '2px', border: '1px solid #d1d5db', borderRadius: '6px', overflow: 'hidden' }}>
              {revisions.map(r => {
                const active = r.ProjectId === project.ProjectId;
                return (
                  <button
                    key={r.ProjectId}
                    onClick={() => { if (!active) navigate(`/projects/${r.ProjectId}`); }}
                    title={`${formatRevLabel(r.RevisionNumber)} — ${r.StatusName}${r.IsLatest ? ' (latest)' : ''}`}
                    style={{
                      border: 'none',
                      padding: '4px 10px',
                      fontSize: '12px',
                      fontWeight: active ? 600 : 400,
                      background: active ? '#4f46e5' : '#fff',
                      color: active ? '#fff' : '#374151',
                      cursor: active ? 'default' : 'pointer',
                    }}
                  >
                    {formatRevLabel(r.RevisionNumber)}{r.IsLatest ? '*' : ''}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {isLatest && canEdit && (
            <button
              className="btn btn-secondary"
              disabled={creatingRevision}
              onClick={handleCreateRevision}
              title="Create a new revision: copies scopes/line items/bids and locks the current version"
            >
              {creatingRevision ? 'Creating…' : '+ Create Revision'}
            </button>
          )}
          <button className="btn btn-secondary" onClick={async () => {
            try {
              const data = await api.getProject(id);
              const { ProjectId, DateBidSubmitted, DateAwarded, CreatedAt, UpdatedAt, RevisionGroupId, RevisionNumber, ...rest } = data;
              navigate('/projects/new', { state: { project: { ...rest, StatusId: 1 } } });
            } catch (err) {
              alert('Error duplicating: ' + err.message);
            }
          }}>Duplicate</button>
          {canManage(user) && <button
            className={`cm-priority-toggle ${project.IsPriority ? 'active' : ''}`}
            onClick={async () => {
              try {
                await api.togglePriority(id, !project.IsPriority);
                loadProject();
                loadActivity();
              } catch (err) { alert('Failed: ' + err.message); }
            }}
            title={project.IsPriority ? 'Remove Must Win' : 'Mark as Must Win'}
          >
            <img src="/favicon.png" alt="" />
            {project.IsPriority ? 'Must Win' : 'Set Must Win'}
          </button>}
          {isLatest && canEdit && <button className="btn btn-primary" onClick={() => setEditing(true)}>Edit Project</button>}
          {canManage(user) && (
            <button
              className="btn btn-secondary"
              onClick={async () => {
                const action = project.IsArchived ? 'Unarchive' : 'Archive';
                if (!window.confirm(`${action} ${formatBidNumber(project.BidNumber, project.RevisionNumber)} — ${project.ProjectName}?`)) return;
                try {
                  await api.archiveProject(id, !project.IsArchived);
                  if (!project.IsArchived) { navigate('/'); } else { loadProject(); loadActivity(); }
                } catch (err) { alert(`${action} failed: ` + err.message); }
              }}
            >
              {project.IsArchived ? 'Unarchive' : 'Archive'}
            </button>
          )}
          {isLatest && canManage(user) && (
            <button
              className="btn btn-secondary"
              disabled={deleting}
              onClick={handleDelete}
              style={{ color: '#dc2626', borderColor: '#dc2626' }}
              title={revisions.length <= 1 ? 'Delete this project and all data' : `Delete ${formatRevLabel(project.RevisionNumber)} and unlock previous revision`}
            >
              {deleting ? 'Deleting…' : revisions.length <= 1 ? 'Delete Project' : `Delete ${formatRevLabel(project.RevisionNumber)}`}
            </button>
          )}
        </div>
      </div>

      <div className={`page-content ${project.IsPriority ? 'must-win-page' : ''}`}>
        <Breadcrumbs items={[
          { label: 'Home', path: '/' },
          { label: 'All Projects', path: '/projects' },
          { label: `${displayBid} - ${project.ProjectName}` },
        ]} />

        {!isLatest && (
          <div style={{ background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: '8px', padding: '10px 16px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#92400e' }}>
            <span style={{ fontSize: '16px' }}>&#128274;</span>
            <span><strong>Revision locked.</strong> This is {formatRevLabel(project.RevisionNumber)} &mdash; edits are only allowed on the current revision{latestRevision ? ` (${formatRevLabel(latestRevision.RevisionNumber)})` : ''}.</span>
            {latestRevision && (
              <button className="btn btn-secondary btn-sm" style={{ marginLeft: 'auto' }} onClick={() => navigate(`/projects/${latestRevision.ProjectId}`)}>
                Go to {formatRevLabel(latestRevision.RevisionNumber)}
              </button>
            )}
          </div>
        )}

        {project.RevisionNumber > 1 && (
          <RevisionSummaryBanner project={project} revisions={revisions} onRegenerate={() => { loadProject(); loadRevisions(); }} />
        )}

        {project.IsArchived && (
          <div style={{ background: '#f3f4f6', border: '1px solid #9ca3af', borderRadius: '8px', padding: '10px 16px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#4b5563' }}>
            <span style={{ fontSize: '16px' }}>&#128451;</span>
            <span><strong>Archived.</strong> This project has been archived and is hidden from normal views.</span>
            {canManage(user) && (
              <button className="btn btn-secondary btn-sm" style={{ marginLeft: 'auto' }} onClick={async () => {
                try { await api.archiveProject(id, false); loadProject(); loadActivity(); } catch (err) { alert('Unarchive failed: ' + err.message); }
              }}>Unarchive</button>
            )}
          </div>
        )}

        {project.IsPriority && (
          <div className="must-win-banner">
            <div className="must-win-banner-inner">
              <img src="/favicon.png" alt="" className="must-win-banner-icon" />
              <div>
                <div className="must-win-banner-title">Must Win</div>
                <div className="must-win-banner-sub">This project has been designated as a priority Bid by leadership.</div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'bidleveling' && (
          <BidLeveling projectId={project.ProjectId} projectName={project.ProjectName} bidNumber={displayBid} />
        )}

        {activeTab === 'details' && <>

        {/* ─── PROJECT INFO — horizontal strip ─── */}
        <div className={`card ${project.IsPriority ? 'cm-priority-card' : ''}`} style={{ marginBottom: '16px' }}>
          <div className="card-header">
            <h3>Project Info</h3>
            <CardEditButton card="info" editCards={editCards} savingCard={savingCard} locked={!isLatest} canEdit={canEdit}
              onEdit={() => startEdit('info')} onDone={() => saveCard('info')} onCancel={() => cancelEdit('info')} />
          </div>
          <div className="card-body">
            {editCards.info ? (
              <div className="inline-edit-grid">
                <InlineField label="Address">
                  <input type="text" value={draft?.Address || ''} onChange={e => updateDraft('Address', e.target.value)} />
                </InlineField>
                <InlineField label="City">
                  <input type="text" value={draft?.City || ''} onChange={e => updateDraft('City', e.target.value)} />
                </InlineField>
                <InlineField label="State">
                  <select value={draft?.State || ''} onChange={e => updateDraft('State', e.target.value)}>
                    <option value="">Select...</option>
                    {[['AL','Alabama'],['AK','Alaska'],['AZ','Arizona'],['AR','Arkansas'],['CA','California'],['CO','Colorado'],['CT','Connecticut'],['DE','Delaware'],['FL','Florida'],['GA','Georgia'],['HI','Hawaii'],['ID','Idaho'],['IL','Illinois'],['IN','Indiana'],['IA','Iowa'],['KS','Kansas'],['KY','Kentucky'],['LA','Louisiana'],['ME','Maine'],['MD','Maryland'],['MA','Massachusetts'],['MI','Michigan'],['MN','Minnesota'],['MS','Mississippi'],['MO','Missouri'],['MT','Montana'],['NE','Nebraska'],['NV','Nevada'],['NH','New Hampshire'],['NJ','New Jersey'],['NM','New Mexico'],['NY','New York'],['NC','North Carolina'],['ND','North Dakota'],['OH','Ohio'],['OK','Oklahoma'],['OR','Oregon'],['PA','Pennsylvania'],['RI','Rhode Island'],['SC','South Carolina'],['SD','South Dakota'],['TN','Tennessee'],['TX','Texas'],['UT','Utah'],['VT','Vermont'],['VA','Virginia'],['WA','Washington'],['WV','West Virginia'],['WI','Wisconsin'],['WY','Wyoming']].map(([c,n]) => <option key={c} value={c}>{n}</option>)}
                  </select>
                </InlineField>
                <InlineField label="Zip">
                  <input type="text" value={draft?.Zip || ''} onChange={e => updateDraft('Zip', e.target.value)} maxLength={10} />
                </InlineField>
                <InlineField label="Project Type">
                  <select value={draft?.ProjectTypeId || ''} onChange={e => updateDraft('ProjectTypeId', e.target.value ? Number(e.target.value) : null)}>
                    <option value="">Select type...</option>
                    {lookups.projectTypes.map(t => <option key={t.ProjectTypeId} value={t.ProjectTypeId}>{t.TypeName}</option>)}
                  </select>
                </InlineField>
                <InlineField label="Building SqFt">
                  <input type="number" min="0" value={draft?.BuildingSqFt ?? ''} onChange={e => updateDraft('BuildingSqFt', e.target.value === '' ? null : Number(e.target.value))} />
                </InlineField>
                <InlineField label="Site SqFt">
                  <input type="number" min="0" value={draft?.SiteSqFt ?? ''} onChange={e => updateDraft('SiteSqFt', e.target.value === '' ? null : Number(e.target.value))} />
                </InlineField>
                <InlineField label="Bid Type">
                  <select value={draft?.BidType || ''} onChange={e => updateDraft('BidType', e.target.value)}>
                    <option value="">Select bid type...</option>
                    <option value="Competitive">Competitive</option>
                    <option value="Negotiated">Negotiated</option>
                    <option value="Not Applicable">Not Applicable</option>
                  </select>
                </InlineField>
                <InlineField label="Contract Type">
                  <select value={draft?.ContractType || ''} onChange={e => updateDraft('ContractType', e.target.value || null)}>
                    <option value="">Select...</option>
                    <option value="Stipulated Sum">Stipulated Sum</option>
                    <option value="Cost Plus">Cost Plus</option>
                    <option value="Cost Plus G-Max">Cost Plus G-Max</option>
                    <option value="Design Build">Design Build</option>
                    <option value="CM At Risk">CM At Risk</option>
                  </select>
                </InlineField>
                {canManage(user) && (
                  <InlineField label="Win Probability">
                    <select value={draft?.WinProbability || ''} onChange={e => updateDraft('WinProbability', e.target.value || null)}>
                      <option value="">Unrated</option>
                      <option value="High">High</option>
                      <option value="Low">Low</option>
                      <option value="Medium">Medium</option>
                    </select>
                  </InlineField>
                )}
              </div>
            ) : (
              <>
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--color-gray-500)', marginBottom: '4px' }}>{displayBid}</div>
                  <div style={{ fontSize: '22px', fontWeight: 700, color: 'var(--color-black)', letterSpacing: '-0.5px' }}>{project.ProjectName}</div>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px 40px' }}>
                  <DetailPill label="Location" value={[project.Address, project.City, project.State, project.Zip].filter(Boolean).join(', ')} />
                  <DetailPill label="Project Type" value={project.ProjectType} />
                  <DetailPill label="Building SqFt" value={project.BuildingSqFt ? project.BuildingSqFt.toLocaleString() : null} />
                  <DetailPill label="Site SqFt" value={project.SiteSqFt ? project.SiteSqFt.toLocaleString() : null} />
                  <DetailPill label="Bid Type" value={project.BidType} />
                  <DetailPill label="Contract Type" value={project.ContractType} />
                  {canManage(user) && <DetailPill label="Win Probability" value={project.WinProbability
                    ? <span className={`win-prob-badge win-prob-${project.WinProbability.toLowerCase()}`}>{project.WinProbability}</span>
                    : <span style={{ color: 'var(--color-gray-500)', fontStyle: 'italic' }}>Unrated</span>} />}
                  <DetailPill label="Plans Distributed" value={project.Distributed ? 'Yes' : 'No'} />
                </div>
              </>
            )}
          </div>
        </div>

        {/* ─── KEY DATES — horizontal timeline ─── */}
        <div className="card detail-dates-card" style={{ marginBottom: '16px' }}>
          <div className="card-header">
            <h3>Key Dates</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              {project.BufferHours && !editCards.dates && <span className="buffer-badge">{project.BufferHours}hr lead time</span>}
              <CardEditButton card="dates" editCards={editCards} savingCard={savingCard} locked={!isLatest} canEdit={canEdit}
                onEdit={() => startEdit('dates')} onDone={() => saveCard('dates')} onCancel={() => cancelEdit('dates')} />
            </div>
          </div>
          <div className="card-body">
            {editCards.dates ? (
              <div className="inline-edit-grid">
                <InlineField label="ITB Received"><DateTimePicker value={draft?.ITBReceived} onChange={v => updateDraft('ITBReceived', v)} /></InlineField>
                <InlineField label="Plans Distributed"><label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}><input type="checkbox" checked={!!draft?.Distributed} onChange={e => updateDraft('Distributed', e.target.checked)} /> Distributed</label></InlineField>
                <InlineField label="Bid Walk Date"><DateTimePicker value={draft?.BidWalkDate} onChange={v => updateDraft('BidWalkDate', v)} /></InlineField>
                <InlineField label="Sub Bids Due"><DateTimePicker value={draft?.SubDueDate} onChange={v => updateDraft('SubDueDate', v)} showTime /></InlineField>
                <InlineField label="Est. Bid Commencement"><DateTimePicker value={draft?.EstBidStart} onChange={v => updateDraft('EstBidStart', v)} /></InlineField>
                <InlineField label="Bid Due to Manager"><DateTimePicker value={draft?.InternalDeadline} onChange={v => updateDraft('InternalDeadline', v)} showTime /></InlineField>
                <InlineField label="Bid Review Lead Time (hrs)">
                  <select value={draft?.BufferHours ?? ''} onChange={e => updateDraft('BufferHours', e.target.value === '' ? null : Number(e.target.value))}>
                    <option value="">None</option>
                    <option value="2">2 hours</option>
                    <option value="6">6 hours</option>
                    <option value="12">12 hours</option>
                    <option value="24">24 hours</option>
                  </select>
                </InlineField>
                <InlineField label="Owner Bid Submission Deadline"><DateTimePicker value={draft?.GCOwnerDeadline} onChange={v => updateDraft('GCOwnerDeadline', v)} showTime /></InlineField>
                <InlineField label="Date Bid Submitted to Owner"><DateTimePicker value={draft?.DateBidSubmitted} onChange={v => updateDraft('DateBidSubmitted', v)} showTime /></InlineField>
                <InlineField label="Date Awarded"><DateTimePicker value={draft?.DateAwarded} onChange={v => updateDraft('DateAwarded', v)} /></InlineField>
              </div>
            ) : (
              <div className="date-timeline">
                {timeline.map((item, i) => {
                  const days = daysUntil(item.raw);
                  const isPast = item.done;
                  const isUrgent = days !== null && days >= 0 && days <= 2 && !isPast;
                  const isWarning = days !== null && days > 2 && days <= 5 && !isPast;
                  const isFuture = days !== null && days > 5 && !isPast;
                  const isMissing = !item.raw;
                  return (
                    <div key={i} className={`date-timeline-item ${isPast ? 'done' : ''} ${isUrgent ? 'urgent' : ''} ${isWarning ? 'warning' : ''} ${isMissing ? 'missing' : ''}`}>
                      <div className="date-timeline-marker">
                        <div className={`marker-dot ${isPast ? 'done' : ''} ${isUrgent ? 'urgent' : ''} ${isWarning ? 'warning' : ''}`} />
                        {i < timeline.length - 1 && <div className={`marker-line ${isPast ? 'done' : ''}`} />}
                      </div>
                      <div className="date-timeline-content">
                        <div className="date-label">{item.label}</div>
                        <div className="date-value">{item.value || 'Not set'}</div>
                        {item.buffer && !isMissing && <div className="date-buffer">{item.buffer}hr lead time before submission</div>}
                        {days !== null && !isPast && (
                          <span className={`date-countdown ${isUrgent ? 'urgent' : ''} ${isWarning ? 'warning' : ''} ${isFuture ? 'future' : ''}`}>
                            {days === 0 ? 'TODAY' : days === 1 ? 'TOMORROW' : days < 0 ? `${Math.abs(days)}d overdue` : `${days} days`}
                          </span>
                        )}
                        {isPast && item.raw && <span className="date-complete">Complete</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* ─── Tools & Integrations row ─── */}
        {activeTab === 'details' && (
          <div style={{ display: 'grid', gridTemplateColumns: alphaFlags.bidLeveling ? '2fr 1fr 1fr' : '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            {alphaFlags.bidLeveling && (
              <BidLevelingCard projectId={project.ProjectId} onOpen={() => { setActiveTab('bidleveling'); window.scrollTo(0, 0); }} />
            )}
            <ExternalLinkCard
              title="Procore"
              desc="Project management, documents & field tools"
              icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>}
              color="#f97316"
              href={`https://app.procore.com`}
            />
            <ExternalLinkCard
              title="NetSuite"
              desc="Financials, cost codes & accounting"
              icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>}
              color="#0891b2"
              href={`https://system.netsuite.com`}
            />
          </div>
        )}

        {/* ─── Bid Leveling full workspace ─── */}
        {activeTab === 'bidleveling' && (
          <div style={{ marginBottom: '16px' }}>
            <button className="btn btn-secondary btn-sm" onClick={() => { setActiveTab('details'); window.scrollTo(0, 0); }}>
              &larr; Back to Project Details
            </button>
          </div>
        )}

        {/* ─── 3-col: Client/Owner | Team | Financials ─── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>

          {/* Client / Owner Info */}
          <div className="card">
            <div className="card-header">
              <h3>Client / Owner</h3>
              <CardEditButton card="client" editCards={editCards} savingCard={savingCard} locked={!isLatest} canEdit={canEdit}
                onEdit={() => startEdit('client')} onDone={() => saveCard('client')} onCancel={() => cancelEdit('client')} />
            </div>
            <div className="card-body">
              {editCards.client ? (
                <>
                  <InlineField label="Client / Owner">
                    <input type="text" value={draft?.ClientName || ''} onChange={e => updateDraft('ClientName', e.target.value)} />
                  </InlineField>
                  <InlineField label="Owner Type">
                    <select value={draft?.OwnerTypeId || ''} onChange={e => updateDraft('OwnerTypeId', e.target.value ? Number(e.target.value) : null)}>
                      <option value="">Select...</option>
                      {lookups.ownerTypes.map(t => <option key={t.OwnerTypeId} value={t.OwnerTypeId}>{t.TypeName}</option>)}
                    </select>
                  </InlineField>
                  <InlineField label="Contact Name">
                    <input type="text" value={draft?.ClientContactName || ''} onChange={e => updateDraft('ClientContactName', e.target.value)} />
                  </InlineField>
                  <InlineField label="Phone">
                    <input type="text" value={draft?.ClientPhone || ''} onChange={e => updateDraft('ClientPhone', e.target.value)} />
                  </InlineField>
                  <InlineField label="Email">
                    <input type="email" value={draft?.ClientEmail || ''} onChange={e => updateDraft('ClientEmail', e.target.value)} />
                  </InlineField>
                </>
              ) : (
              <>
              <div className="detail-field"><div className="detail-field-label">Client</div><div className="detail-field-value bold">{project.ClientName || '-'}</div></div>
              <div className="detail-field"><div className="detail-field-label">Owner Type</div><div className="detail-field-value">{project.OwnerType || '-'}</div></div>
              {project.ClientContactName && <div className="detail-field"><div className="detail-field-label">Contact Name</div><div className="detail-field-value">{project.ClientContactName}</div></div>}
              {project.ClientPhone && <div className="detail-field"><div className="detail-field-label">Phone</div><div className="detail-field-value">{project.ClientPhone}</div></div>}
              {project.ClientEmail && <div className="detail-field"><div className="detail-field-label">Email</div><div className="detail-field-value"><a href={`mailto:${project.ClientEmail}`} style={{ color: 'var(--color-accent)' }}>{project.ClientEmail}</a></div></div>}
              </>
              )}
            </div>
          </div>

          {/* Team */}
          <div className="card">
            <div className="card-header">
              <h3>Team</h3>
              <CardEditButton card="team" editCards={editCards} savingCard={savingCard} locked={!isLatest} canEdit={canEdit}
                onEdit={() => startEdit('team')} onDone={() => saveCard('team')} onCancel={() => cancelEdit('team')} />
            </div>
            <div className="card-body">
              {editCards.team ? (
                <>
                  <InlineField label="Estimator">
                    <PersonAutocomplete
                      options={lookups.estimators}
                      value={draft?.EstimatorId || ''}
                      currentLabel={project.Estimator}
                      onChange={id => updateDraft('EstimatorId', id ? Number(id) : null)}
                      placeholder="Search users…"
                    />
                  </InlineField>
                  <InlineField label="Estimating Coordinator">
                    <PersonAutocomplete
                      options={lookups.coordinators}
                      value={draft?.BidCoordinatorId || ''}
                      currentLabel={project.BidCoordinator}
                      onChange={id => updateDraft('BidCoordinatorId', id ? Number(id) : null)}
                      placeholder="Search users…"
                    />
                  </InlineField>
                  <InlineField label="Project Executive">
                    <select value={draft?.ProjectExecutiveId || ''} onChange={e => updateDraft('ProjectExecutiveId', e.target.value ? Number(e.target.value) : null)}>
                      <option value="">Select...</option>
                      {lookups.pes.map(t => <option key={t.TeamMemberId} value={t.TeamMemberId}>{t.FullName}</option>)}
                    </select>
                  </InlineField>
                  <InlineField label="Project Manager">
                    <select value={draft?.ProjectManagerId || ''} onChange={e => updateDraft('ProjectManagerId', e.target.value ? Number(e.target.value) : null)}>
                      <option value="">Select...</option>
                      {lookups.pms.map(t => <option key={t.TeamMemberId} value={t.TeamMemberId}>{t.FullName}</option>)}
                    </select>
                  </InlineField>
                  <InlineField label="Final Reviewer">
                    <select value={draft?.FinalReviewerId || ''} onChange={e => updateDraft('FinalReviewerId', e.target.value ? Number(e.target.value) : null)}>
                      <option value="">Select...</option>
                      {lookups.reviewers.map(t => <option key={t.TeamMemberId} value={t.TeamMemberId}>{t.FullName}</option>)}
                    </select>
                  </InlineField>
                </>
              ) : (
                <>
                  <div className="detail-field">
                    <div className="detail-field-label">Estimator</div>
                    <div className="detail-field-value bold">{project.Estimator || '-'}</div>
                  </div>
                  <div className="detail-field">
                    <div className="detail-field-label">Estimating Coordinator</div>
                    <div className="detail-field-value bold">{project.BidCoordinator || '-'}</div>
                  </div>
                  <div className="detail-field">
                    <div className="detail-field-label">Project Executive</div>
                    <div className="detail-field-value">{project.ProjectExecutive || '-'}</div>
                  </div>
                  <div className="detail-field">
                    <div className="detail-field-label">Project Manager</div>
                    <div className="detail-field-value">{project.ProjectManager || '-'}</div>
                  </div>
                  <div className="detail-field">
                    <div className="detail-field-label">Final Reviewer</div>
                    <div className="detail-field-value">{project.FinalReviewer || '-'}</div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Financials */}
          <div className="card">
            <div className="card-header">
              <h3>Financials</h3>
              <CardEditButton card="financials" editCards={editCards} savingCard={savingCard} locked={!isLatest} canEdit={canEdit}
                onEdit={() => startEdit('financials')} onDone={() => saveCard('financials')} onCancel={() => cancelEdit('financials')} />
            </div>
            <div className="card-body">
              {editCards.financials ? (
                <>
                  <InlineField label="Bid Amount">
                    <input type="number" min="0" step="0.01" value={draft?.ProjectCost ?? ''} onChange={e => updateDraft('ProjectCost', e.target.value === '' ? null : Number(e.target.value))} />
                  </InlineField>
                  <InlineField label="Projected Bid Amount">
                    <input type="number" min="0" step="0.01" value={draft?.EstActualContractValue ?? ''} onChange={e => updateDraft('EstActualContractValue', e.target.value === '' ? null : Number(e.target.value))} />
                  </InlineField>
                  <InlineField label="Awarded GC">
                    <input type="text" value={draft?.AwardedGC || ''} onChange={e => updateDraft('AwardedGC', e.target.value)} />
                  </InlineField>
                </>
              ) : (
                <>
                  <div className="detail-field">
                    <div className="detail-field-label">Bid Amount</div>
                    <div className="detail-field-value big">{formatCurrency(project.ProjectCost)}</div>
                  </div>
                  <div className="detail-field">
                    <div className="detail-field-label">Projected Bid Amount</div>
                    <div className="detail-field-value big">{formatCurrency(project.EstActualContractValue)}</div>
                  </div>
                  {project.AwardedGC && (
                    <div className="detail-field">
                      <div className="detail-field-label">Awarded GC</div>
                      <div className="detail-field-value">{project.AwardedGC}</div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* ─── Notes + Activity — 2-col ─── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          {/* Notes & Follow-Up */}
          <div className="card">
            <div className="card-header">
              <h3>Notes & Follow-Up</h3>
              <CardEditButton card="notes" editCards={editCards} savingCard={savingCard} locked={!isLatest} canEdit={canEdit}
                onEdit={() => startEdit('notes')} onDone={() => saveCard('notes')} onCancel={() => cancelEdit('notes')} />
            </div>
            <div className="card-body">
              {editCards.notes ? (
                <>
                  <InlineField label="Notes">
                    <textarea rows={4} value={draft?.Notes || ''} onChange={e => updateDraft('Notes', e.target.value)} placeholder="General notes..." />
                  </InlineField>
                  <InlineField label="Follow Up">
                    <input type="text" value={draft?.FollowUp || ''} onChange={e => updateDraft('FollowUp', e.target.value)} placeholder="Follow-up reminder..." />
                  </InlineField>
                  <InlineField label="Competitors">
                    <input type="text" value={draft?.Competitors || ''} onChange={e => updateDraft('Competitors', e.target.value)} placeholder="Known competitors..." />
                  </InlineField>
                </>
              ) : (
                <>
                  {project.Notes ? (
                    <div className="detail-field">
                      <div className="detail-field-label">Notes</div>
                      <div className="detail-field-value" style={{ whiteSpace: 'pre-wrap' }}>{project.Notes}</div>
                    </div>
                  ) : null}
                  {project.FollowUp ? (
                    <div className="detail-field">
                      <div className="detail-field-label">Follow Up</div>
                      <div className="detail-field-value">{project.FollowUp}</div>
                    </div>
                  ) : null}
                  {project.Competitors ? (
                    <div className="detail-field">
                      <div className="detail-field-label">Competitors</div>
                      <div className="detail-field-value">{project.Competitors}</div>
                    </div>
                  ) : null}
                  {!project.Notes && !project.FollowUp && !project.Competitors && (
                    <p style={{ fontSize: '13px', color: '#999' }}>No notes yet.</p>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Activity Log — last 7, scrollable, PST timezone */}
          <div className="card">
            <div className="card-header">
              <h3>Activity Log</h3>
              <span className="badge-count">{activity.length}</span>
            </div>
            <div className="card-body" style={{ padding: 0 }}>
              {canEdit && (
                <div style={{ display: 'flex', gap: '8px', padding: '12px 16px', borderBottom: '1px solid var(--color-gray-100)' }}>
                  <input
                    type="text"
                    className="filter-input"
                    style={{ flex: 1 }}
                    placeholder="Add a note..."
                    value={newNote}
                    onChange={e => setNewNote(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleAddNote()}
                  />
                  <button className="btn btn-primary btn-sm" onClick={handleAddNote} style={project.IsPriority ? { background: '#92752E', borderColor: '#92752E' } : undefined}>Add</button>
                </div>
              )}
              <div style={{ maxHeight: '380px', overflowY: 'auto' }}>
                {activity.length === 0 ? (
                  <p style={{ fontSize: '13px', color: '#999', padding: '16px', textAlign: 'center' }}>No activity yet.</p>
                ) : activity.slice(0, 7).map(a => {
                  const pst = new Date(a.CreatedAt).toLocaleString('en-US', {
                    timeZone: 'America/Los_Angeles',
                    month: 'short', day: 'numeric', year: 'numeric',
                    hour: 'numeric', minute: '2-digit',
                  });
                  const isNote = a.ActivityType === 'Note';
                  return (
                    <div key={a.ActivityId} style={{
                      padding: '10px 16px',
                      borderBottom: '1px solid var(--color-gray-100)',
                      borderLeft: isNote ? '3px solid var(--color-accent)' : '3px solid transparent',
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                        <strong style={{ fontSize: '12px', color: 'var(--color-black)' }}>{a.CreatedBy || 'System'}</strong>
                        <span style={{ fontSize: '10px', color: 'var(--color-gray-500)', whiteSpace: 'nowrap' }}>{pst} PST</span>
                      </div>
                      <div style={{ fontSize: '13px', color: 'var(--color-gray-700)', marginTop: '3px', lineHeight: 1.4 }}>{a.Description}</div>
                      <span style={{
                        display: 'inline-block', marginTop: '4px', fontSize: '9px', fontWeight: 600,
                        textTransform: 'uppercase', letterSpacing: '0.5px',
                        padding: '1px 6px', borderRadius: '3px',
                        background: isNote ? '#dbeafe' : a.ActivityType === 'StatusChange' ? '#fef3c7' : '#f3f4f6',
                        color: isNote ? '#1e40af' : a.ActivityType === 'StatusChange' ? '#92400e' : '#6b7280',
                      }}>{a.ActivityType}</span>
                    </div>
                  );
                })}
                {activity.length > 7 && (
                  <div style={{ padding: '10px 16px', fontSize: '12px', color: 'var(--color-gray-500)', textAlign: 'center', background: 'var(--color-gray-100)' }}>
                    Showing 7 of {activity.length} entries — scroll for more
                  </div>
                )}
                {activity.slice(7).map(a => {
                  const pst = new Date(a.CreatedAt).toLocaleString('en-US', {
                    timeZone: 'America/Los_Angeles',
                    month: 'short', day: 'numeric', year: 'numeric',
                    hour: 'numeric', minute: '2-digit',
                  });
                  const isNote = a.ActivityType === 'Note';
                  return (
                    <div key={a.ActivityId} style={{
                      padding: '10px 16px',
                      borderBottom: '1px solid var(--color-gray-100)',
                      borderLeft: isNote ? '3px solid var(--color-accent)' : '3px solid transparent',
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                        <strong style={{ fontSize: '12px', color: 'var(--color-black)' }}>{a.CreatedBy || 'System'}</strong>
                        <span style={{ fontSize: '10px', color: 'var(--color-gray-500)', whiteSpace: 'nowrap' }}>{pst} PST</span>
                      </div>
                      <div style={{ fontSize: '13px', color: 'var(--color-gray-700)', marginTop: '3px', lineHeight: 1.4 }}>{a.Description}</div>
                      <span style={{
                        display: 'inline-block', marginTop: '4px', fontSize: '9px', fontWeight: 600,
                        textTransform: 'uppercase', letterSpacing: '0.5px',
                        padding: '1px 6px', borderRadius: '3px',
                        background: isNote ? '#dbeafe' : a.ActivityType === 'StatusChange' ? '#fef3c7' : '#f3f4f6',
                        color: isNote ? '#1e40af' : a.ActivityType === 'StatusChange' ? '#92400e' : '#6b7280',
                      }}>{a.ActivityType}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        </>}
      </div>
    </>
  );
}

// Shared Edit/Done toggle used in each card header.
function CardEditButton({ card, editCards, savingCard, onEdit, onDone, onCancel, locked, canEdit = true }) {
  if (locked || !canEdit) return null;
  const isEditing = editCards[card];
  const isSaving = savingCard === card;
  if (!isEditing) {
    return <button className="btn btn-secondary btn-sm" onClick={onEdit}>Edit</button>;
  }
  return (
    <div style={{ display: 'inline-flex', gap: '6px' }}>
      <button className="btn btn-secondary btn-sm" onClick={onCancel} disabled={isSaving}>Cancel</button>
      <button className="btn btn-primary btn-sm" onClick={onDone} disabled={isSaving}>
        {isSaving ? 'Saving...' : 'Done'}
      </button>
    </div>
  );
}

// Single label/input row used inside card edit mode -- reuses detail-field
// layout so the spacing matches the read-only rows above/below it.
function DetailPill({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--color-gray-500)', marginBottom: '2px' }}>{label}</div>
      <div style={{ fontSize: '14px', color: 'var(--color-black)' }}>{value || <span style={{ color: 'var(--color-gray-300)' }}>-</span>}</div>
    </div>
  );
}

function ExternalLinkCard({ title, desc, icon, color, href }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="external-link-card"
      style={{ '--ext-color': color }}
    >
      <div className="external-link-icon" style={{ background: `${color}15`, color }}>
        {icon}
      </div>
      <div style={{ flex: 1 }}>
        <div className="external-link-title">{title}</div>
        <div className="external-link-desc">{desc}</div>
      </div>
      <div className="external-link-arrow" style={{ color }}>↗</div>
    </a>
  );
}

function BidLevelingCard({ projectId, onOpen }) {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.getProjectBidLeveling(projectId)
      .then(data => {
        const scopes = data?.scopes || [];
        const totalBids = scopes.reduce((sum, s) => sum + (s.bids?.length || 0), 0);
        setStats({ scopeCount: scopes.length, bidCount: totalBids });
      })
      .catch(() => setStats({ scopeCount: 0, bidCount: 0 }));
  }, [projectId]);

  return (
    <div className="bid-leveling-entry-card" onClick={onOpen}>
      <div className="bid-leveling-entry-left">
        <div className="bid-leveling-entry-icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
            <rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/>
          </svg>
        </div>
        <div>
          <div className="bid-leveling-entry-title">Bid Leveling</div>
          <div className="bid-leveling-entry-desc">
            Scope comparison, sub-contractor tracking & line items
          </div>
          {stats && (
            <div className="bid-leveling-entry-stats">
              <span>{stats.scopeCount} scope{stats.scopeCount !== 1 ? 's' : ''}</span>
              <span>&middot;</span>
              <span>{stats.bidCount} sub bid{stats.bidCount !== 1 ? 's' : ''}</span>
            </div>
          )}
        </div>
      </div>
      <div className="bid-leveling-entry-right">
        <span style={{ fontSize: '9px', fontWeight: 700, background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: '3px', letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: '6px', display: 'inline-block' }}>Alpha</span>
        <div className="bid-leveling-entry-open">Open &rarr;</div>
      </div>
    </div>
  );
}

function InlineField({ label, children }) {
  return (
    <div className="detail-field detail-field-edit">
      <div className="detail-field-label">{label}</div>
      <div className="detail-field-value">{children}</div>
    </div>
  );
}

function RevisionSummaryBanner({ project, revisions, onRegenerate }) {
  const [generating, setGenerating] = useState(false);
  const [expanded, setExpanded] = useState(true);

  const allSummaries = revisions
    .filter(r => r.RevisionNumber > 1 && r.RevisionSummary)
    .sort((a, b) => a.RevisionNumber - b.RevisionNumber);

  async function generate() {
    setGenerating(true);
    try {
      await api.generateRevisionSummary(project.ProjectId);
      onRegenerate();
    } catch (err) {
      alert('Failed to generate summary: ' + err.message);
    }
    setGenerating(false);
  }

  return (
    <div className="revision-banner">
      <div className="revision-banner-header" onClick={() => setExpanded(e => !e)}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="revision-banner-icon"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
        <span className="revision-banner-title">Revision History</span>
        <span className="revision-banner-rev">{formatRevLabel(project.RevisionNumber)}</span>
        <button className={`revision-banner-toggle ${expanded ? 'is-open' : ''}`}>{expanded ? 'Hide' : 'Show'}</button>
      </div>

      {expanded && (
        <div className="revision-banner-body">
          {allSummaries.length > 0 && (
            <div className="revision-banner-list">
              {allSummaries.map(r => (
                <div key={r.ProjectId} className="revision-banner-item">
                  <span className="revision-banner-rev-pill">{formatRevLabel(r.RevisionNumber)}</span>
                  <span className="revision-banner-summary">{r.RevisionSummary}</span>
                </div>
              ))}
            </div>
          )}

          {!project.RevisionSummary && (
            <div style={{ marginTop: '10px' }}>
              <button className="btn btn-secondary btn-sm" onClick={generate} disabled={generating}>
                {generating ? 'Generating...' : 'Generate AI Summary'}
              </button>
              <span className="revision-banner-hint">Compares {formatRevLabel(project.RevisionNumber)} vs {formatRevLabel(project.RevisionNumber - 1)}</span>
            </div>
          )}

          {project.RevisionSummary && (
            <button className="btn btn-secondary btn-sm" onClick={generate} disabled={generating} style={{ marginTop: '8px', fontSize: '11px' }}>
              {generating ? 'Regenerating...' : 'Regenerate'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
